import type { IntlShape } from "react-intl";
import type { CreateFileDataInput, UpdateListingDataInput } from "@/graphql/generated/operations";
import type { UploadedFile } from "@/components/share/uploaded-file";
import { LISTING_IMAGE_TOO_LARGE, LISTING_IMAGE_UNSUPPORTED } from "@/lib/genesis-error-codes";
import { graphQlErrorExtensions } from "@/lib/graphql-error";
import { imageDataUrl, type ImageValue } from "@/lib/image-data-url";
import { formatMegabytes, MAX_INLINE_CONTENT_PER_REQUEST, MAX_LISTING_IMAGE_BYTES } from "@/lib/upload-limits";

// Die Bilder einer Ausschreibung: was das Formular davon an genesis schickt, und in welchen Paketen.
//
// Das Formular hält die Bilder wie ein Beitrag (ShareCreatePage): eine Liste, in der genau eines die
// Vorschau ist. genesis kennt zwei Felder, das Titelbild (`mainImage`) und die weiteren Bilder
// (`images`, angehängt; entfernt über `imagesToDelete`). Wird ein weiteres Bild zur Vorschau, geht es
// als Titelbild neu hinaus und seine alte Zeile wird gelöscht; wird das bisherige Titelbild zu einem
// weiteren Bild, geht es als weiteres Bild neu hinaus.
//
// Jedes Bild reist base64 im Mutationsrumpf, und das BFF nimmt höchstens 16 MB je Anfrage. Ein Bild
// darf 5 MiB haben, base64 knapp 7 MB: zwei passen in eine Anfrage, drei nie. Deshalb geht mit dem
// ersten Schreiben (Anlegen oder Speichern) nur mit, was neben Titelbild und Text noch passt, und der
// Rest folgt in eigenen updateListing-Anfragen, jede unter derselben Grenze.

type ImageFields = Pick<UpdateListingDataInput, "mainImage" | "images" | "imagesToDelete">;

export interface ListingImageWrites {
  /** Geht mit dem ersten Schreiben hinaus, neben den Textfeldern. Leer, wenn sich nichts geändert hat. */
  first: ImageFields;
  /** Weitere Bilder, die nicht mehr mitpassten: je Paket ein updateListing, in dieser Reihenfolge. */
  followUps: CreateFileDataInput[][];
}

function asInput(file: UploadedFile): CreateFileDataInput {
  return { fileName: file.name, mimeType: file.mimeType, content: file.content };
}

function size(file: CreateFileDataInput): number {
  return file.content?.length ?? 0;
}

/**
 * Teilt Uploads in Pakete, ohne ihre Reihenfolge zu ändern. Das erste Paket fährt mit einer Anfrage,
 * die schon anderes trägt, und hat deshalb sein eigenes, kleineres Budget; es darf leer bleiben. Jedes
 * weitere Paket nimmt mindestens ein Bild, auch eines über dem Budget: teilen lässt es sich nicht, und
 * die Vorprüfung hält jedes Bild weit darunter.
 */
export function packUploads(
  uploads: readonly CreateFileDataInput[],
  firstBudget: number,
  budget: number,
): { head: CreateFileDataInput[]; rest: CreateFileDataInput[][] } {
  const head: CreateFileDataInput[] = [];
  let used = 0;
  let index = 0;
  for (; index < uploads.length; index++) {
    const upload = uploads[index]!;
    if (used + size(upload) > firstBudget) break;
    head.push(upload);
    used += size(upload);
  }
  const rest: CreateFileDataInput[][] = [];
  let pack: CreateFileDataInput[] = [];
  used = 0;
  for (; index < uploads.length; index++) {
    const upload = uploads[index]!;
    if (pack.length > 0 && used + size(upload) > budget) {
      rest.push(pack);
      pack = [];
      used = 0;
    }
    pack.push(upload);
    used += size(upload);
  }
  if (pack.length > 0) rest.push(pack);
  return { head, rest };
}

/**
 * Was vom Stand `initial` (leer beim Anlegen) zum Stand `current` an genesis gehen muss. Ein Feld,
 * das sich nicht ändert, fehlt: ein weggelassenes `mainImage` lässt das Titelbild stehen, `null`
 * entfernt es.
 */
export function listingImageWrites(
  initial: readonly UploadedFile[],
  current: readonly UploadedFile[],
  budget: number = MAX_INLINE_CONTENT_PER_REQUEST,
): ListingImageWrites {
  const first: ImageFields = {};
  const main = current.find((file) => file.isMain) ?? null;
  const hadMain = initial.some((file) => file.existingRole === "main");

  let promotedId: string | null = null;
  if (main?.existingRole !== "main") {
    if (main?.content) {
      first.mainImage = asInput(main);
      if (main.existingRole === "gallery" && main.existingId) promotedId = main.existingId;
    } else if (hadMain) {
      // Keine Vorschau mehr, oder eine, deren Bytes nicht geladen wurden: das alte Titelbild geht.
      first.mainImage = null;
    }
  }

  const kept = new Set(current.map((file) => file.existingId).filter(Boolean));
  const imagesToDelete = initial
    .filter((file) => file.existingRole === "gallery" && file.existingId && !kept.has(file.existingId))
    .map((file) => file.existingId as string);
  if (promotedId) imagesToDelete.push(promotedId);
  if (imagesToDelete.length > 0) first.imagesToDelete = imagesToDelete;

  // Neu hochgeladene weitere Bilder und ein zum weiteren Bild gewordenes Titelbild, in der Reihenfolge
  // der Kacheln. genesis hängt sie in Anfragereihenfolge an.
  const uploads = current
    .filter((file) => file !== main && file.content && (!file.existingId || file.existingRole === "main"))
    .map(asInput);
  const firstBudget = budget - (first.mainImage ? size(first.mainImage) : 0);
  const { head, rest } = packUploads(uploads, firstBudget, budget);
  if (head.length > 0) first.images = head;
  return { first, followUps: rest };
}

/** Wie viele Bilder die Pakete zusammen tragen. */
export function countImages(packages: readonly (readonly CreateFileDataInput[])[]): number {
  return packages.reduce((sum, pack) => sum + pack.length, 0);
}

/**
 * Die Antwort auf eine Bild-Ablehnung von genesis, oder null, wenn es keine war. Beide Codes nennen
 * eine Reparatur, die bei der Person liegt (kleinere Datei, anderes Format); "versuch es noch einmal"
 * hülfe bei keinem.
 */
export function listingImageErrorMessage(intl: IntlShape, error: unknown): string | null {
  if (graphQlErrorExtensions(error, LISTING_IMAGE_TOO_LARGE)) {
    return intl.formatMessage(
      {
        id: "share.listings.images.tooLarge",
        defaultMessage: "Ein Bild ist größer als {max}. Bitte wähle eine kleinere Datei.",
      },
      { max: formatMegabytes(MAX_LISTING_IMAGE_BYTES) },
    );
  }
  if (graphQlErrorExtensions(error, LISTING_IMAGE_UNSUPPORTED)) {
    return intl.formatMessage({
      id: "share.listings.images.unsupported",
      defaultMessage: "Ein Bild ist kein PNG, JPEG, GIF, BMP, TIFF oder WebP. Bitte wähle eine andere Datei.",
    });
  }
  return null;
}

/**
 * Was eine Detailseite zeigt: das Titelbild zuerst, dann die weiteren Bilder, nur Bilder mit Bytes.
 * Liest genesis sie der Person nicht vor (Empfänger einer nicht offenen Ausschreibung), kommt null,
 * und es bleibt eine leere Liste.
 */
export function gallerySources(listing: {
  mainImage?: ImageValue | null;
  images?: readonly ImageValue[] | null;
}): string[] {
  return [listing.mainImage, ...(listing.images ?? [])]
    .map((image) => imageDataUrl(image))
    .filter((src): src is string => src !== null);
}
